import {
	type CompetitorItem,
	type EventLockState,
	type EventMedalCeremony,
	type EventMedalCeremonyBody,
	type EventMedals,
	type EventPodiumBody,
	type EventStructure,
	type Keyset,
	type MedalChangedItem,
	type MedalEventItem,
	type MedalListItem,
	type MedalNoMedalsItem,
	type MedalPendingItem,
	type MedalProgress,
	type MedalReviewOrder,
	type MedalTally,
	type MeetDetail,
	PAGE_SIZE_MAX,
	type Paginated,
	type PhaseStandings,
} from "@seagames/contracts";
import { type QueryClient, infiniteQueryOptions, keepPreviousData, queryOptions } from "@tanstack/react-query";
import { api } from "../api/client";
import { DESK_POLL_MS, DESK_STALE_MS, SCHEDULE_STALE_MS, retryTransient } from "../api/queries";

/**
 * Every read and write of the medal desk. One request per screen part, each
 * bounded: the queues are offset pages, the medal list is a keyset, the
 * podium under review is ONE event (its structure, its podium, and the
 * standings of its ranked medal phases — a handful).
 */

export const deskKeys = {
	all: ["desk"] as const,
	queue: (list: DeskList, gamesId: string, page: number, pageSize: number, filters: DeskFilters = {}) => ["desk", list, gamesId, page, pageSize, filters] as const,
	count: (list: DeskList, gamesId: string) => ["desk", list, gamesId, "count"] as const,
	event: (eventId: string) => ["desk", "event", eventId] as const,
	structure: (eventId: string) => ["desk", "structure", eventId] as const,
	standings: (phaseId: string) => ["desk", "standings", phaseId] as const,
	meet: (meetId: string) => ["desk", "meet", meetId] as const,
	roster: (eventId: string) => ["desk", "roster", eventId] as const,
	medals: (filters: MedalFilters) => ["medals", "list", filters] as const,
	tally: (gamesId: string, sportId?: string) => ["medals", "tally", gamesId, sportId ?? null] as const,
};

/**
 * The desk's lists: decided events not yet settled, awarded events that moved
 * since, the awarded events themselves (newest release first, from the medal
 * board) — and the events it settled with NO medal, which are on no other
 * list: without theirs, one wrong "no medals" or a podium emptied by mistake
 * would leave the event on no screen at all.
 */
export type DeskList = "pending" | "changed" | "awarded" | "noMedals";
export const DESK_PAGE_SIZE = 25;

/** The queue's search, sport and grouping (`?q=`, `?sport=`, `?order=`). */
export interface DeskFilters {
	q?: string | undefined;
	sport_id?: string | undefined;
	order?: MedalReviewOrder | undefined;
}

/** Each list's API path (`GET /medals/<path>`) and its tab of Award medals. Awarded reads the board (`/medals/events?state=AWARDED`). */
const DESK_LIST_PATH: Record<DeskList, string> = { pending: "pending", changed: "changed", awarded: "events", noMedals: "no-medals" };
export const DESK_LIST_ROUTE = {
	pending: "/award",
	changed: "/award/changed",
	awarded: "/award/awarded",
	noMedals: "/award/no-medals",
} as const satisfies Record<DeskList, string>;
export type DeskRoute = (typeof DESK_LIST_ROUTE)[DeskList];

export type DeskQueueItem = MedalPendingItem | MedalChangedItem | MedalNoMedalsItem | MedalEventItem;
type QueueItem<L extends DeskList> = L extends "pending"
	? MedalPendingItem
	: L extends "changed"
		? MedalChangedItem
		: L extends "awarded"
			? MedalEventItem
			: MedalNoMedalsItem;

/** The query string of a list's page: the review lists take `order`; the board (awarded) takes its own state and order. */
function listQuery(list: DeskList, gamesId: string, page: number, pageSize: number, f: DeskFilters) {
	const common = { games_id: gamesId, page, page_size: pageSize, q: f.q, sport_id: f.sport_id };
	return list === "awarded" ? { ...common, state: "AWARDED", sort: "recent" } : { ...common, order: f.order === "sport" ? "sport" : undefined };
}

/** `GET /medals/pending` / `/changed` / `/no-medals` (or the board's awarded events) — one offset page of one games. */
export function deskQueueQuery<L extends DeskList>(list: L, gamesId: string, page: number, pageSize = DESK_PAGE_SIZE, filters: DeskFilters = {}) {
	return queryOptions({
		queryKey: deskKeys.queue(list, gamesId, page, pageSize, filters),
		queryFn: ({ signal }) =>
			api<Paginated<QueueItem<L>>>("GET", `/medals/${DESK_LIST_PATH[list]}`, { query: listQuery(list, gamesId, page, pageSize, filters), signal }),
		staleTime: DESK_STALE_MS,
		placeholderData: keepPreviousData,
		retry: retryTransient,
	});
}

/** How many events a list holds — the tabs' and the sidebar's badge. A one-row page: the total comes with it. */
export function deskCountQuery(list: DeskList, gamesId: string) {
	return queryOptions({
		queryKey: deskKeys.count(list, gamesId),
		queryFn: async ({ signal }) => {
			const page = await api<Paginated<unknown>>("GET", `/medals/${DESK_LIST_PATH[list]}`, { query: listQuery(list, gamesId, 1, 1, {}), signal });
			return page.total;
		},
		staleTime: DESK_STALE_MS,
		refetchInterval: DESK_POLL_MS,
		retry: retryTransient,
	});
}

/** `GET /meets/:id` — the deciding meet's detail, for its venue in the workspace header. One meet, cached. */
export function meetDetailQuery(meetId: string) {
	return queryOptions({
		queryKey: deskKeys.meet(meetId),
		queryFn: ({ signal }) => api<MeetDetail>("GET", `/meets/${encodeURIComponent(meetId)}`, { signal }),
		staleTime: SCHEDULE_STALE_MS,
		retry: retryTransient,
	});
}

/**
 * The event's entries with their rosters (`GET /events/:id/competitors`, the
 * largest page) — a team row's roster on the results board, fetched the
 * first time one is expanded. A team event has tens of entries at most: one
 * bounded page, cached for the event.
 */
export function eventRosterQuery(eventId: string) {
	return queryOptions({
		queryKey: deskKeys.roster(eventId),
		queryFn: ({ signal }) =>
			api<Paginated<CompetitorItem>>("GET", `/events/${encodeURIComponent(eventId)}/competitors`, {
				query: { page: 1, page_size: PAGE_SIZE_MAX },
				signal,
			}),
		staleTime: SCHEDULE_STALE_MS,
		retry: retryTransient,
	});
}

/** `GET /events/:id/medals` — the podium, OVR's suggestion as stored, the lock, the version, the warnings. */
export function eventMedalsQuery(eventId: string) {
	return queryOptions({
		queryKey: deskKeys.event(eventId),
		queryFn: ({ signal }) => api<EventMedals>("GET", `/events/${encodeURIComponent(eventId)}/medals`, { signal }),
		staleTime: DESK_STALE_MS,
		retry: retryTransient,
	});
}

/** `GET /events/:id/structure` — phases → meets → slots: the pickers and the computed suggestion. */
export function eventStructureQuery(eventId: string) {
	return queryOptions({
		queryKey: deskKeys.structure(eventId),
		queryFn: ({ signal }) => api<EventStructure>("GET", `/events/${encodeURIComponent(eventId)}/structure`, { signal }),
		staleTime: DESK_STALE_MS,
		retry: retryTransient,
	});
}

/** `GET /phases/:id/standings` — only for a ranked medal phase of several meets (`podiumStandingsPhases`). */
export function phaseStandingsQuery(phaseId: string) {
	return queryOptions({
		queryKey: deskKeys.standings(phaseId),
		queryFn: ({ signal }) => api<PhaseStandings>("GET", `/phases/${encodeURIComponent(phaseId)}/standings`, { signal }),
		staleTime: DESK_STALE_MS,
		retry: retryTransient,
	});
}

/** After a desk write: refresh the queues and their counts, the medal list and the tally (`["medals"]`). */
export async function refreshAfterDeskWrite(qc: QueryClient): Promise<void> {
	await Promise.all([qc.invalidateQueries({ queryKey: deskKeys.all }), qc.invalidateQueries({ queryKey: ["medals"] })]);
}

/** `PUT /events/:id/medals` — the WHOLE podium, with the version the desk loaded. THE award. */
export const putPodium = (eventId: string, body: EventPodiumBody) =>
	api<EventMedals>("PUT", `/events/${encodeURIComponent(eventId)}/medals`, { body });

/** `POST /events/:id/lock` / `unlock` — unlocking needs a reason. */
export const setEventLock = (eventId: string, lock: boolean, reason: string | undefined) =>
	api<EventLockState>("POST", `/events/${encodeURIComponent(eventId)}/${lock ? "lock" : "unlock"}`, { body: reason ? { reason } : {} });

/** `PUT /events/:id/medal-ceremony`. */
export const putCeremony = (eventId: string, body: EventMedalCeremonyBody) =>
	api<EventMedalCeremony>("PUT", `/events/${encodeURIComponent(eventId)}/medal-ceremony`, { body });

// ─── Awarded medals and the tally ────────────────────────────────────────────

export interface MedalFilters {
	games_id: string;
	sport_id?: string | undefined;
	responsible_organization_id?: string | undefined;
}

export const MEDAL_LIST_PAGE = 50;

/** `GET /medals` — awarded medals, newest first; one keyset page per "Show more". */
export function medalListQuery(filters: MedalFilters) {
	const limit = Math.min(PAGE_SIZE_MAX, MEDAL_LIST_PAGE);
	return infiniteQueryOptions({
		queryKey: deskKeys.medals(filters),
		queryFn: ({ pageParam, signal }) =>
			api<Keyset<MedalListItem>>("GET", "/medals", { query: { ...filters, limit, cursor: pageParam ?? undefined }, signal }),
		initialPageParam: null as string | null,
		getNextPageParam: (last) => (last.has_more && last.next_cursor ? last.next_cursor : null),
		staleTime: SCHEDULE_STALE_MS,
		retry: retryTransient,
	});
}

/**
 * `GET /medals/progress` — where every medal event of the games stands, in
 * total and per sport: ONE statement. Under `["medals"]`, so every desk write
 * refreshes it (`refreshAfterDeskWrite`); polled like the queue counts, since
 * OVR making a meet official moves it with no desk write.
 */
export function progressQuery(gamesId: string) {
	return queryOptions({
		queryKey: ["medals", "progress", gamesId] as const,
		queryFn: ({ signal }) => api<MedalProgress>("GET", "/medals/progress", { query: { games_id: gamesId }, signal }),
		staleTime: DESK_STALE_MS,
		refetchInterval: DESK_POLL_MS,
		retry: retryTransient,
	});
}

/** `GET /medals/tally` — one row per organisation of the games (tens), ranked by the API. */
export function tallyQuery(gamesId: string, sportId?: string) {
	return queryOptions({
		queryKey: deskKeys.tally(gamesId, sportId),
		queryFn: ({ signal }) => api<MedalTally>("GET", "/medals/tally", { query: { games_id: gamesId, sport_id: sportId }, signal }),
		staleTime: DESK_STALE_MS,
		retry: retryTransient,
	});
}
